import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentType, UNKNOWN_ANCHOR_TITLE } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ItemSelector } from '@/components/widget/item-selector';
import { useContentList } from '@/hooks/useContentQueries';
import { useInstantiateTemplate, useRunWriteUpTask, useTemplatesList } from '@/hooks/useTemplateQueries';
import { toContentHref } from '@/utils/content-ref';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'pending_approval' }
  | { kind: 'failed'; error: string };

interface NewFromTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedTemplateId?: number;
}

function defaultInstanceTitle(templateName: string): string {
  return `${templateName}, ${new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export function NewFromTemplateDialog({
  open,
  onOpenChange,
  preselectedTemplateId,
}: NewFromTemplateDialogProps) {
  const navigate = useNavigate();
  const [templateId, setTemplateId] = useState<number | null>(preselectedTemplateId ?? null);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [writeUp, setWriteUp] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });

  const templatesQuery = useTemplatesList();
  const published = (templatesQuery.data?.templates ?? []).filter(
    (entry) => entry.status === 'PUBLISHED'
  );
  const anchorsQuery = useContentList({ contentTypes: [ContentType.ANCHOR], limit: 100 });

  const selected = published.find((entry) => entry.templateId === templateId) ?? null;

  useEffect(() => {
    if (open && preselectedTemplateId !== undefined) {
      setTemplateId(preselectedTemplateId);
    }
  }, [open, preselectedTemplateId]);

  useEffect(() => {
    if (!titleTouched && selected !== null) {
      setTitle(defaultInstanceTitle(selected.name));
    }
  }, [selected, titleTouched]);

  const resetForm = () => {
    setTemplateId(preselectedTemplateId ?? null);
    setTitle('');
    setTitleTouched(false);
    setAnchorId(null);
    setWriteUp(false);
    setSubmitState({ kind: 'idle' });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const instantiate = useInstantiateTemplate();
  const runWriteUp = useRunWriteUpTask();

  const handleSubmit = async () => {
    if (submitState.kind === 'submitting') return;
    if (templateId === null) {
      setSubmitState({ kind: 'failed', error: 'Choose a published template.' });
      return;
    }
    if (title.trim().length < 3) {
      setSubmitState({ kind: 'failed', error: 'Enter a title for the new document.' });
      return;
    }
    setSubmitState({ kind: 'submitting' });
    const response = await instantiate.mutateAsync({
      templateId,
      title: title.trim(),
      ...(anchorId !== null ? { anchorId } : {}),
      writeUp,
    });
    if (response.status === 'PENDING_APPROVAL') {
      setSubmitState({ kind: 'pending_approval' });
      return;
    }
    if (response.status !== 'EXECUTED' || !response.output) {
      setSubmitState({ kind: 'failed', error: response.error ?? 'Starting the instance failed.' });
      return;
    }
    handleOpenChange(false);
    navigate(toContentHref(response.output.instanceCode));
    if (writeUp) {
      void runWriteUp({
        instanceContentId: response.output.instanceContentId,
        instanceTitle: title.trim(),
      });
    }
  };

  const toSelectorItem = (item: { id: number; title?: string | null }) => ({
    id: item.id,
    title: typeof item.title === 'string' ? item.title : null,
  });
  const anchorItems = (anchorsQuery.data?.items ?? [])
    .map(toSelectorItem)
    .filter((item) => item.title !== UNKNOWN_ANCHOR_TITLE);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New from template</DialogTitle>
          <DialogDescription>
            Starts an instance through the same governed tool Neo uses. The sections seed from the
            template; the write-up fills them from attached evidence.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Template</Label>
              <ItemSelector
                entityName="template"
                displayName="Template"
                items={published.map((entry) => ({ id: entry.templateId, title: entry.name }))}
                selectedId={templateId}
                onSelect={setTemplateId}
                loading={templatesQuery.isLoading}
              />
              {published.length === 0 && !templatesQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  No published templates yet — publish a template first.
                </p>
              ) : null}
              {selected !== null ? (
                <p className="text-sm text-muted-foreground">{selected.whenToUse}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-from-template-title">Title</Label>
              <Input
                id="new-from-template-title"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setTitleTouched(true);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Anchor (optional)</Label>
              <ItemSelector
                entityName="anchor"
                displayName="Anchor"
                items={anchorItems}
                selectedId={anchorId}
                onSelect={setAnchorId}
                loading={anchorsQuery.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Without one, the instance files in the hub's Unknown bucket and can be re-filed later.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={writeUp} onCheckedChange={(value) => setWriteUp(value === true)} />
              Write it up straight away
            </label>
            {submitState.kind === 'failed' ? (
              <p className="text-sm text-destructive">{submitState.error}</p>
            ) : null}
            {submitState.kind === 'pending_approval' ? (
              <p className="text-sm text-muted-foreground">
                Awaiting approval in Mission Control. The instance will be created once an admin
                approves it.
              </p>
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitState.kind === 'submitting' || submitState.kind === 'pending_approval'}
          >
            {submitState.kind === 'submitting' ? 'Starting…' : 'Start'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
